import { Form } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useLazyGetClientServiceCategoryByIdQuery,
  useUpdateClientServiceCategoryMutation,
} from '@app/api/ClientServices';
import { CATEGORIES_PATH } from '@app/constants';
import { SUCCESS, WARNING } from '@app/constants/alerts';
import { useUserPrint } from '@app/hooks';
import { useUnsavedData } from '@app/hooks/useUnsavedData';
import { LeavePageContext, NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NClientServices } from '@app/types/ClientServices';

const { useForm, useWatch } = Form;

export const useAddServiceCategory = () => {
  const { activeBranchId } = useUserPrint();
  const [getClientServiceCategoryById] =
    useLazyGetClientServiceCategoryByIdQuery();
  const [updateClientServiceCategory] =
    useUpdateClientServiceCategoryMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [form] = useForm();
  const [serverError, setServerError] = useState('');
  const [category, setCategory] = useState<NClientServices.Category>();
  const [searchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(false);
  const editableCategoryId = Number(searchParams.get('id'));
  const { showNotification } = useContext(NotificationContext);
  const navigate = useNavigate();

  const { prepareStartedData, checkIsDataUpdated, setInputData } =
    useContext(LeavePageContext);

  useEffect(() => {
    const fetchCategoryData = async () => {
      if (editableCategoryId) {
        setIsLoading(true);
        try {
          const data = await getClientServiceCategoryById({
            ActiveBranchId: activeBranchId,
            categoryId: editableCategoryId,
          }).unwrap();

          form.setFieldsValue({
            Name: data.Name,
            Description: data.Description,
            IsDeleted: data.IsDeleted,
          });

          setCategory(data);
          prepareStartedData(data);
        } catch (error) {
          showNotification(handleCatchError(error));
        } finally {
          setIsLoading(false);
        }
      } else {
        prepareStartedData({
          Name: undefined,
          Description: undefined,
        });
      }
    };

    fetchCategoryData();
  }, [
    editableCategoryId,
    activeBranchId,
    form,
    prepareStartedData,
    showNotification,
    getClientServiceCategoryById,
  ]);

  const observedData = useWatch([], form);

  const { saveData } = useUnsavedData({
    data: observedData,
    skip: isLoading,
  });
  const submitForm = useCallback(
    async (values: Pick<NClientServices.Category, 'Name' | 'Description'>) => {
      setIsLoading(true);

      try {
        await updateClientServiceCategory({
          ActiveBranchId: activeBranchId,
          data: {
            Id: category?.Id || 0,
            Name: values.Name,
            Description: values.Description,
            BranchId: activeBranchId || 0,
            IsDeleted: form.getFieldValue('IsDeleted'),
            MigrateId: null,
          },
        }).unwrap();

        saveData();

        showNotification(
          `Категория ${editableCategoryId ? 'обновлена' : 'создана'} успешно`,
          'success'
        );
        navigate(-1 || CATEGORIES_PATH);
      } catch (error) {
        const { status, data } = error as { status?: string; data?: string };

        if (status === 'ERROR') {
          showNotification(data || '', 'error');
        } else if (status === 'WARNING') {
          setServerError(data || '');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      activeBranchId,
      category?.Id,
      form,
      saveData,
      showNotification,
      navigate,
      editableCategoryId,
      updateClientServiceCategory,
    ]
  );

  const handleCancel = () => {
    checkIsDataUpdated({
      path: '/categories',
      navigate,
    });
  };

  const deleteCategory = async () => {
    setIsLoading(true);
    try {
      await updateClientServiceCategory({
        ActiveBranchId: activeBranchId,
        data: {
          Id: category?.Id || 0,
          Name: form.getFieldValue('Name'),
          Description: form.getFieldValue('Description'),
          BranchId: activeBranchId || 0,
          IsDeleted: true,
          MigrateId: null,
        },
      }).unwrap();
      showNotification('Категория удалена успешно', SUCCESS);
      navigate(-1 || CATEGORIES_PATH);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
      setServerError(handleCatchError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const initialValues = useMemo(
    () =>
      editableCategoryId
        ? category
        : {
            Name: '',
            Description: '',
          },
    [category, editableCategoryId]
  );

  return {
    isLoading,
    form,
    submitForm,
    serverError,
    setServerError,
    editableCategoryId,
    modalOpen,
    setModalOpen,
    handleCancel,
    deleteCategory,
    setInputData,
    initialValues,
  };
};
